import { getHeaderSortClassName } from 'helpers/tableSort';
import React from 'react';
import { getAbbreviatedUnit } from 'helpers/unitsAbbreviation';
import ViewIcon from 'assets/open_in_new.svg';
import infoIcon from 'assets/info.svg';
import ClickableDiv from 'components/ClickableDiv';
import { Link } from 'react-router-dom';
import { paths } from 'routes/paths';
import formatNumber from 'helpers/formatNumber';
import LinkIcon from 'components/Icons/linkIcon';
import {
  SEASON_IDS_PARAM,
  SELECTED_CROP_SEASONS_STORAGE_KEY
} from 'utilities/cropSeasonUrl';
import convertDateString from '../../helpers/dateFormat';

/**
 * The product view is product-scoped (shows applications across seasons) but the
 * Applications list is season-scoped. For a plain left click (a same-tab
 * navigation) seed the application's crop season(s) into localStorage before
 * React Router pushes, so the destination list and crop-season breadcrumb mount
 * on the application's own season instead of the currently-selected one.
 *
 * Modifier / non-primary clicks open a new tab, which reads the season(s) from
 * the `?seasonIds` URL param at load instead (see applyUrlCropSeasons). Seeding
 * on those would wrongly change the current tab's season, so we mirror React
 * Router's own navigation check and skip them.
 */
export const seedCropSeasonsForSameTabNav = (event, cropSeasonIds) => {
  if (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.altKey ||
    event.ctrlKey ||
    event.shiftKey
  ) {
    return;
  }

  if (cropSeasonIds?.length) {
    localStorage.setItem(
      SELECTED_CROP_SEASONS_STORAGE_KEY,
      JSON.stringify(cropSeasonIds)
    );
  }
};

const buildViewPath = (id, recordName, cropSeasonIds) => {
  const params = new URLSearchParams();
  if (recordName) {
    params.set('search', recordName);
  }
  // Ctrl+Click opens a new tab that only inherits the URL, not the router
  // state, so carry the id in the query too (SummaryParamsProvider reads it).
  if (id) {
    params.set('operationId', id);
  }
  // Carry the application's crop season(s) so a new tab (which only inherits the
  // URL) lands on the application's own season; applyUrlCropSeasons reads this at
  // load. Same-tab clicks seed localStorage directly (seedCropSeasonsForSameTabNav).
  if (cropSeasonIds?.length) {
    params.set(SEASON_IDS_PARAM, cropSeasonIds.join(','));
  }

  return {
    pathname: paths.applications,
    search: params.toString() ? `?${params.toString()}` : '',
    state: {
      operationId: id
    }
  };
};

const dynamicTableColumns = (
  disableRender,
  selectionInfo,
  sortingInfo,
  dataInfo
) => {
  const {
    isAllInPageRecordsSelected,
    selectAllRows,
    isAllRecordsSelected
  } = selectionInfo;
  const { sortBy, sortDir } = sortingInfo;
  const { filteredAndSortedTasks, reactUiKitTablePaginationProp } = dataInfo;

  const getColSpan = (record, index) =>
    (record.banner || isAllInPageRecordsSelected) && index === 0 ? 0 : 1;
  const columns = [
    {
      title: 'Application ID',
      dataIndex: 'name',
      key: 'name',
      className: getHeaderSortClassName('name', sortBy, sortDir),
      sorter: true,
      sortOrder: sortBy === 'name' ? sortDir : null,
      onCell: (record, index) => {
        return {
          colSpan:
            (record.banner || isAllInPageRecordsSelected) && index === 0 ? 7 : 1
        };
      },
      render: (fieldValue, record) => {
        if (record.isInfoRow) {
          return (
            <div
              className="text-sm remove-parent-padding bg-blue-10 flex flex-row items-center"
              colSpan={7}
            >
              <p className="inline-flex items-center px-5 py-3">
                <img
                  alt="info"
                  src={infoIcon}
                  className="mr-2"
                  style={{ width: '24px', height: '24px' }}
                />
                {!isAllRecordsSelected
                  ? 'All records on this page are selected.'
                  : 'All records are selected'}
                {!isAllRecordsSelected && (
                  <span className="text-blue-60 font-semibold cursor-pointer ml-1">
                    <ClickableDiv
                      onClick={() =>
                        selectAllRows(filteredAndSortedTasks, 'key')
                      }
                    >
                      {` Select all ${reactUiKitTablePaginationProp.total} records`}
                    </ClickableDiv>
                  </span>
                )}
              </p>
            </div>
          );
        }
        if (record.banner) {
          return record.banner;
        }
        return <div>{fieldValue}</div>;
      }
    },
    {
      title: 'Completed Date',
      dataIndex: 'completedDate',
      key: 'completedDate',
      className: getHeaderSortClassName('completedDate', sortBy, sortDir),
      sorter: true,
      sortOrder: sortBy === 'completedDate' ? sortDir : null,
      onCell: (record, index) => ({
        colSpan: getColSpan(record, index)
      }),
      render: fieldValue => <div>{convertDateString(fieldValue)}</div>
    },
    {
      title: 'Rate',
      dataIndex: 'ratePerArea',
      key: 'ratePerArea',
      className: getHeaderSortClassName('ratePerArea', sortBy, sortDir),
      sorter: true,
      sortOrder: sortBy === 'ratePerArea' ? sortDir : null,
      onCell: (record, index) => ({
        colSpan: getColSpan(record, index)
      }),
      render: (fieldValue, record) => (
        <div>
          {`${formatNumber(
            fieldValue,
            getAbbreviatedUnit(record.ratePerAreaUnit)
          )}/${getAbbreviatedUnit(record.appliedAreaUnit)}`}
        </div>
      )
    },
    {
      title: 'Applied Quantity',
      dataIndex: 'appliedArea',
      key: 'appliedArea',
      className: getHeaderSortClassName('appliedArea', sortBy, sortDir),
      sorter: true,
      sortOrder: sortBy === 'appliedArea' ? sortDir : null,
      onCell: (record, index) => ({
        colSpan: getColSpan(record, index)
      }),
      render: (fieldValue, record) => (
        <div>{`${formatNumber(
          fieldValue,
          getAbbreviatedUnit(record.productQuantityUnit)
        )}`}</div>
      )
    },
    {
      title: 'Unit Price',
      dataIndex: 'averagePriceAtTimeOfCreation',
      key: 'averagePriceAtTimeOfCreation',
      className: getHeaderSortClassName(
        'averagePriceAtTimeOfCreation',
        sortBy,
        sortDir
      ),
      sorter: true,
      sortOrder: sortBy === 'averagePriceAtTimeOfCreation' ? sortDir : null,
      onCell: (record, index) => ({
        colSpan: getColSpan(record, index)
      }),
      render: (fieldValue, record) => (
        <div>
          {`$${formatNumber(
            fieldValue,
            getAbbreviatedUnit(record.averagePriceUnitAtTimeOfCreation)
          )?.replace(' ', ' / ')}`}
        </div>
      )
    }
  ];
  if (!disableRender) {
    columns.push({
      title: '',
      dataIndex: 'taskId',
      key: 'taskId',
      className: getHeaderSortClassName('taskId', sortBy, sortDir),
      onCell: (record, index) => ({
        colSpan: getColSpan(record, index)
      }),
      render: (_, record) => (
        <div className="w-full flex flex-row justify-end">
          <Link
            to={buildViewPath(
              record.applicationId,
              record.name,
              record.cropSeasonIds
            )}
            onClick={event =>
              seedCropSeasonsForSameTabNav(event, record.cropSeasonIds)
            }
            className="flex items-center"
          >
            <LinkIcon />
            <span className="ml-1">View</span>
          </Link>
        </div>
      )
    });
  }
  return columns;
};

export default dynamicTableColumns;

export const oldTableColumns = (sortBy, sortDir) => [
  {
    title: 'Application ID',
    dataIndex: 'name',
    key: 'name',
    className: getHeaderSortClassName('name', sortBy, sortDir),
    sorter: true,
    sortOrder: sortBy === 'name' ? sortDir : null,
    render: fieldValue => <div>{fieldValue}</div>
  },
  {
    title: 'Completed Date',
    dataIndex: 'completedDate',
    key: 'completedDate',
    className: getHeaderSortClassName('completedDate', sortBy, sortDir),
    sorter: true,
    sortOrder: sortBy === 'completedDate' ? sortDir : null,
    render: fieldValue => <div>{new Date(fieldValue).toLocaleDateString()}</div>
  },
  {
    title: 'Rate',
    dataIndex: 'ratePerArea',
    key: 'ratePerArea',
    className: getHeaderSortClassName('ratePerArea', sortBy, sortDir),
    sorter: true,
    sortOrder: sortBy === 'ratePerArea' ? sortDir : null,
    render: (fieldValue, record) => (
      <div>
        {`${formatNumber(
          fieldValue,
          getAbbreviatedUnit(record.ratePerAreaUnit)
        )}/${getAbbreviatedUnit(record.appliedAreaUnit)}`}
      </div>
    )
  },
  {
    title: 'Applied Area',
    dataIndex: 'appliedArea',
    key: 'appliedArea',
    className: getHeaderSortClassName('appliedArea', sortBy, sortDir),
    sorter: true,
    sortOrder: sortBy === 'appliedArea' ? sortDir : null,
    render: (fieldValue, record) => (
      <div>{`${formatNumber(
        fieldValue,
        getAbbreviatedUnit(record.appliedAreaUnit)
      )}`}</div>
    )
  },
  {
    title: 'Applied Quantity',
    dataIndex: 'totalProduct',
    key: 'totalProduct',
    className: getHeaderSortClassName('totalProduct', sortBy, sortDir),
    sorter: true,
    sortOrder: sortBy === 'totalProduct' ? sortDir : null,
    render: (fieldValue, record) => (
      <div>{`${formatNumber(
        fieldValue,
        getAbbreviatedUnit(record.totalProductUnit)
      )}`}</div>
    )
  },
  {
    title: '',
    dataIndex: 'taskId',
    key: 'taskId',
    className: getHeaderSortClassName('taskId', sortBy, sortDir),
    render: (_, record) => (
      <div className="w-full flex flex-row justify-end">
        <Link
          to={buildViewPath(
            record.applicationId,
            record.name,
            record.cropSeasonIds
          )}
          onClick={event =>
            seedCropSeasonsForSameTabNav(event, record.cropSeasonIds)
          }
          className="flex flex-row"
        >
          <img className="mr-1" src={ViewIcon} alt="view icon" />
          <span>View</span>
        </Link>
      </div>
    )
  }
];
